import { createContext, useContext, useEffect, useState } from "react";
import Lenis from "lenis";
// Ships Lenis's own (small, additive-only) stylesheet — adds
// `overscroll-behavior: contain` to [data-lenis-prevent] elements (stops
// a nested panel from scroll-chaining into the page once it hits its own
// scroll boundary) and a couple of defensive states (iframe pointer-events
// while smooth-scrolling, height:auto on html.lenis). Imported once, here,
// rather than copied — see lenis/dist/lenis.css for the exact rules.
import "lenis/dist/lenis.css";

// Module-level constant, never recreated per-render — passing a fresh
// object into the Lenis constructor on every render is a known footgun
// that causes it to re-initialize in a loop if it were ever used as an
// effect dependency directly (we depend on `reducedMotion` instead, so
// this constant only needs to be referentially stable, not diffed).
//
// lerp-based damping (not duration+easing) — chosen so the feel comes from
// one tunable knob. 0.09 sits just under Lenis's own default (0.1): a hair
// smoother without drifting into "floaty". wheelMultiplier 0.9 softens wheel
// steps slightly for the same reason. `anchors: true` lets Lenis intercept
// same-page hash-link *clicks* itself (see index.css's `scroll-padding-top`,
// which Lenis's scrollTo reads directly via getComputedStyle — no separate
// JS offset needed); it does NOT cover a hash already present in the URL on
// initial load or on a route change, which AnimatedRoutes (src/App.js)
// handles explicitly. `stopInertiaOnNavigate: true` kills any in-flight
// wheel momentum the instant an internal link is clicked, so it can't bleed
// into the next route.
//
// `autoRaf: false` (speed Phase 0, 2026-10-08). Lenis's own loop runs a
// requestAnimationFrame on EVERY frame for as long as the page is open,
// scrolling or not — measured at roughly a quarter of an idle mid-range
// phone's main thread. SmoothScroll drives Lenis itself instead, and only
// while something is actually moving: see startScrollLoop below.
//
// NOTE: an earlier brief this was built from also listed `respectReducedMotion:
// true`, but that option does not exist on this installed version (lenis
// 1.3.25 — checked node_modules/lenis/dist/lenis.d.ts's LenisOptions).
// Passing it would be a silent no-op. Reduced motion is instead handled by
// never constructing a Lenis instance at all — see usePrefersReducedMotion
// and the effect below — which is strictly stronger than any internal flag:
// it guarantees zero smoothing code runs, not just that it's suppressed.
const LENIS_OPTIONS = {
  autoRaf: false,
  smoothWheel: true,
  lerp: 0.09,
  wheelMultiplier: 0.9,
  anchors: true,
  stopInertiaOnNavigate: true,
  syncTouch: false, // leave touch on native momentum — iOS-style decel is a desktop-wheel-only feel
};

function usePrefersReducedMotion() {
  const getInitial = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  const [reduced, setReduced] = useState(getInitial);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    // Safari < 14 / old Edge only have the deprecated addListener API.
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    if (typeof mq.addListener === "function") {
      mq.addListener(onChange);
      return () => mq.removeListener(onChange);
    }
    return undefined;
  }, []);

  return reduced;
}

// Exposes the live Lenis instance (or null, under reduced motion / before
// mount / if construction failed) to the rest of the app — e.g. AnimatedRoutes
// in src/App.js, which needs it for route-change and hash-link scrolling.
// A Context.Provider is used instead of lenis/react's <ReactLenis> component
// specifically because its VALUE can change (instance -> null -> a new
// instance) without ever changing which component TYPE sits at this position
// in the tree. Conditionally rendering `children` directly vs. wrapping them
// in a component (the previous approach, and lenis/react's own <ReactLenis>
// used the same shape) changes the tree's type at that position whenever the
// condition flips — React does not diff across a type change, so on the very
// next render it discards the entire old subtree and mounts a fresh one.
// Confirmed empirically: toggling reduced motion while the app was open
// remounted every descendant (cart state, form inputs, route state — not
// just scroll position) because of exactly this. Keeping this component
// always mounted, with only its Context value changing, avoids that class of
// bug entirely — `children` here is the exact same element on every render.
const LenisInstanceContext = createContext(null);

export function useSmoothScrollLenis() {
  return useContext(LenisInstanceContext);
}

// Runs Lenis's frame loop only while a scroll is in progress, and stops it
// the moment the scroll settles, so an idle page asks for no frames at all.
//
// Every smooth scroll — a wheel step, an anchor click, a programmatic
// lenis.scrollTo() from AnimatedRoutes — goes through instance.scrollTo
// (Lenis's own wheel handler calls this.scrollTo), so wrapping that one
// method is what wakes the loop. Lenis marks itself scrolling and starts its
// animation synchronously inside scrollTo, so the first frame always finds
// work to do; the loop keeps going while either is true.
//
// Lenis measures each frame from the previous one (`time - this.time`).
// After an idle gap that difference is seconds long, and a damped scroll
// fed a multi-second step lands on its target in one frame — a visible
// snap. Clearing `time` before the first frame makes that frame count as
// zero, exactly like the first frame Lenis ever ran.
export function startScrollLoop(instance, raf = requestAnimationFrame, caf = cancelAnimationFrame) {
  let rafId = 0;
  const moving = () => Boolean(instance.isScrolling) || Boolean(instance.animate?.isRunning);
  const frame = (time) => {
    instance.raf(time);
    rafId = moving() ? raf(frame) : 0;
  };
  const wake = () => {
    if (rafId) return;
    instance.time = 0;
    rafId = raf(frame);
  };
  const scrollTo = instance.scrollTo.bind(instance);
  instance.scrollTo = (...args) => {
    const result = scrollTo(...args);
    wake();
    return result;
  };
  return () => {
    if (rafId) caf(rafId);
    rafId = 0;
  };
}

// Gates Lenis off entirely under reduced motion — falls back to fully
// native scroll, not just a faster/disabled instance — and re-evaluates
// live if the OS-level preference changes while the app stays open.
const SmoothScroll = ({ children }) => {
  const reducedMotion = usePrefersReducedMotion();
  const [lenis, setLenis] = useState(null);

  useEffect(() => {
    if (reducedMotion) return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    let instance;
    try {
      instance = new Lenis(LENIS_OPTIONS);
    } catch (err) {
      // Native scrolling remains fully available either way — Lenis simply
      // never became active. Don't let a construction failure (an
      // unexpectedly incomplete environment, a future breaking browser
      // change, etc.) take the rest of the app down with it.
      console.warn("Lenis failed to initialize; continuing with native scroll.", err);
      return undefined;
    }

    const stopScrollLoop = startScrollLoop(instance);
    setLenis(instance);
    return () => {
      stopScrollLoop();
      instance.destroy();
      setLenis(null);
    };
  }, [reducedMotion]);

  return (
    <LenisInstanceContext.Provider value={lenis}>
      {children}
    </LenisInstanceContext.Provider>
  );
};

export default SmoothScroll;
